import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAdminEventDetail,
  adminRefundTicketSale,
} from "../../services/ingroove/adminService";
import { formatCurrency, formatDate, formatDateTime } from "../../utils/formatters";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Ticket,
  Package,
  TrendingUp,
  CircleDollarSign,
  Users,
  Building2,
  Search,
  X,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const AUDIENCE_PAGE_SIZE = 15;

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const saleStatusVariant = (status) => {
  switch (status) {
    case "Completed": return "success";
    case "Refunded": return "info";
    case "Pending": return "warning";
    case "Failed": return "danger";
    default: return "default";
  }
};

const ProgressBar = ({ sold, total, color = "bg-primary" }) => {
  const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
  return (
    <div className="w-full bg-bg-elevated rounded-full h-1.5 mt-1.5">
      <div
        className={`h-1.5 rounded-full transition-all ${color}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const RefundModal = ({ member, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
    <div className="bg-bg-surface border border-border rounded-2xl p-6 w-full max-w-md space-y-5">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-400/10 rounded-xl">
            <AlertTriangle size={16} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Confirm Refund</h3>
            <p className="text-xs text-text-muted mt-0.5">{member.userName}</p>
          </div>
        </div>
        <button onClick={onCancel} className="text-text-muted hover:text-text-primary transition">
          <X size={16} />
        </button>
      </div>

      <div className="bg-bg-elevated border border-border rounded-xl p-4 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-text-muted">Ticket</span>
          <span className="text-text-primary font-medium">{member.ticketName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-muted">Paid</span>
          <span className="text-text-secondary">{formatCurrency(member.totalAmountPaid)}</span>
        </div>
        <div className="flex justify-between border-t border-border pt-2 mt-1">
          <span className="text-text-muted">Refund amount</span>
          <span className="text-primary font-semibold">{formatCurrency(member.refundableAmount)}</span>
        </div>
        <p className="text-xs text-text-hint pt-1">Banking fees are excluded from the refund.</p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 py-2 rounded-xl border border-border text-sm text-text-secondary hover:border-primary hover:text-primary transition"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(member.saleId)}
          disabled={loading}
          className="flex-1 py-2 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50"
        >
          {loading ? "Processing..." : "Confirm Refund"}
        </button>
      </div>
    </div>
  </div>
);

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("audience"); // "audience" | "tickets" | "extras"

  // Audience filter/pagination
  const [audienceSearch, setAudienceSearch] = useState("");
  const [audienceStatusFilter, setAudienceStatusFilter] = useState("");
  const [audiencePage, setAudiencePage] = useState(1);

  // Refund
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const res = await getAdminEventDetail(id);
      setEvent(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvent(); }, [id]);

  useEffect(() => {
    setAudiencePage(1);
  }, [audienceSearch, audienceStatusFilter]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const handleRefund = async (ticketSaleId) => {
    setRefunding(true);
    try {
      const res = await adminRefundTicketSale(ticketSaleId);
      if (res.isSuccess) {
        showToast("success", "Refund processed successfully.");
        setRefundTarget(null);
        await fetchEvent();
      } else {
        showToast("error", res.message ?? "Refund failed.");
      }
    } finally {
      setRefunding(false);
    }
  };

  if (loading)
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-6 bg-bg-surface rounded w-32" />
        <div className="h-56 bg-bg-surface border border-border rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 bg-bg-surface border border-border rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-bg-surface border border-border rounded-xl" />
      </div>
    );

  if (!event)
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Calendar size={32} className="text-border-hover mb-3" />
        <p className="text-sm text-text-muted">Event not found.</p>
      </div>
    );

  const status = statusConfig[event.status] ?? statusConfig[0];
  const audience = event.audience ?? [];
  const ticketTiers = event.ticketTiers ?? [];
  const extraTiers = event.extraTiers ?? [];

  // Filtered audience
  const filteredAudience = audience.filter((m) => {
    const matchesSearch =
      !audienceSearch ||
      m.userName?.toLowerCase().includes(audienceSearch.toLowerCase()) ||
      m.userEmail?.toLowerCase().includes(audienceSearch.toLowerCase());

    const matchesStatus =
      audienceStatusFilter === "" || m.status === audienceStatusFilter;

    return matchesSearch && matchesStatus;
  });

  const audienceTotalPages = Math.ceil(filteredAudience.length / AUDIENCE_PAGE_SIZE);
  const pagedAudience = filteredAudience.slice(
    (audiencePage - 1) * AUDIENCE_PAGE_SIZE,
    audiencePage * AUDIENCE_PAGE_SIZE
  );

  const ticketProgress = event.totalTicketCapacity > 0
    ? Math.min((event.totalTicketsSold / event.totalTicketCapacity) * 100, 100)
    : 0;

  const statCards = [
    {
      label: "Tickets Sold",
      value: `${event.totalTicketsSold} / ${event.totalTicketCapacity}`,
      sub: `${ticketProgress.toFixed(0)}% capacity`,
      icon: <Ticket size={14} className="text-primary" />,
      accent: "bg-primary/10",
    },
    {
      label: "Extras Sold",
      value: `${event.totalExtrasSold} / ${event.totalExtrasCapacity}`,
      icon: <Package size={14} className="text-secondary" />,
      accent: "bg-secondary/10",
    },
    {
      label: "Total Revenue",
      value: formatCurrency(event.totalRevenue),
      sub: `${formatCurrency(event.ticketRevenue)} tickets · ${formatCurrency(event.extrasRevenue)} extras`,
      icon: <TrendingUp size={14} className="text-green-400" />,
      accent: "bg-green-400/10",
    },
    {
      label: "Fees Collected",
      value: formatCurrency(event.feesCollected),
      icon: <CircleDollarSign size={14} className="text-amber-400" />,
      accent: "bg-amber-400/10",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border text-sm shadow-lg
          ${toast.type === "success"
            ? "bg-bg-surface border-primary/30 text-primary"
            : "bg-bg-surface border-red-400/30 text-red-400"}`}
        >
          {toast.type === "success" ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
          {toast.message}
        </div>
      )}

      {/* Back */}
      <button
        onClick={() => navigate("/ingroove/events")}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-primary transition"
      >
        <ArrowLeft size={13} /> Back to Events
      </button>

      {/* Hero banner */}
      <div className="relative h-56 bg-bg-elevated rounded-2xl overflow-hidden">
        {event.image ? (
          <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar size={40} className="text-border-hover" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-4 left-5 right-5">
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-white truncate">{event.title}</h2>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {event.address && (
                  <div className="flex items-center gap-1.5 text-white/60 text-xs">
                    <MapPin size={11} />
                    {event.address}
                  </div>
                )}
                {event.dates?.length > 0 && (
                  <div className="flex items-center gap-1.5 text-white/60 text-xs">
                    <Calendar size={11} />
                    {event.dates.map((d) => formatDate(d)).join(" · ")}
                  </div>
                )}
              </div>
            </div>
            <Badge label={status.label} variant={status.variant} />
          </div>
        </div>
      </div>

      {/* Hub + owner strip */}
      <div className="bg-bg-surface border border-border rounded-xl px-5 py-4 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          {event.hubImage ? (
            <img src={event.hubImage} alt={event.hubName} className="w-8 h-8 rounded-lg object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-bg-elevated flex items-center justify-center">
              <Building2 size={14} className="text-text-muted" />
            </div>
          )}
          <div>
            <p className="text-xs text-text-muted">Hub</p>
            <button
              onClick={() => navigate(`/ingroove/hubs/${event.hubId}`)}
              className="text-sm text-text-primary font-medium hover:text-primary transition"
            >
              {event.hubName}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-bg-elevated flex items-center justify-center">
            <Users size={14} className="text-text-muted" />
          </div>
          <div>
            <p className="text-xs text-text-muted">Organiser</p>
            <p className="text-sm text-text-primary font-medium">{event.ownerName}</p>
          </div>
        </div>

        {event.hubCoversFees && (
          <div className="ml-auto">
            <Badge label="Hub covers fees" variant="info" />
          </div>
        )}

        <div className="text-xs text-text-hint">
          Created {formatDate(event.createdAt)}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s) => (
          <div
            key={s.label}
            className="bg-bg-surface border border-border rounded-xl p-4 flex items-start gap-3"
          >
            <div className={`p-2 rounded-lg flex-shrink-0 ${s.accent}`}>{s.icon}</div>
            <div className="min-w-0">
              <p className="text-xs text-text-muted">{s.label}</p>
              <p className="text-sm font-semibold text-text-primary mt-0.5 truncate">{s.value}</p>
              {s.sub && <p className="text-xs text-text-hint mt-0.5 truncate">{s.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Description */}
      {event.description && (
        <div className="bg-bg-surface border border-border rounded-xl px-5 py-4">
          <p className="text-xs text-text-muted uppercase tracking-wider mb-2">About</p>
          <p className="text-sm text-text-secondary leading-relaxed">{event.description}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-bg-surface border border-border rounded-xl p-1 w-fit">
        {[
          { key: "audience", label: `Audience (${audience.length})` },
          { key: "tickets", label: `Ticket Tiers (${ticketTiers.length})` },
          { key: "extras", label: `Extras (${extraTiers.length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition ${
              tab === t.key
                ? "ingroove-gradient-bg text-white shadow-glow"
                : "text-text-muted hover:text-text-secondary"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Audience tab */}
      {tab === "audience" && (
        <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Users size={14} className="text-primary" />
              <h3 className="text-sm font-semibold text-text-primary">Audience</h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={audienceSearch}
                  onChange={(e) => setAudienceSearch(e.target.value)}
                  className="pl-8 pr-8 py-1.5 text-xs bg-bg-base border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition w-48"
                />
                {audienceSearch && (
                  <button
                    onClick={() => setAudienceSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-hint hover:text-text-muted"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>

              {/* Status filter */}
              <div className="flex gap-1 bg-bg-base border border-border rounded-xl p-1">
                {[
                  { label: "All", value: "" },
                  { label: "Completed", value: "Completed" },
                  { label: "Refunded", value: "Refunded" },
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setAudienceStatusFilter(f.value)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition ${
                      audienceStatusFilter === f.value
                        ? "ingroove-gradient-bg text-white shadow-glow"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filteredAudience.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users size={24} className="text-border-hover mb-2" />
              <p className="text-xs text-text-muted">No audience members found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {pagedAudience.map((member) => (
                <div
                  key={member.saleId}
                  className="flex items-center justify-between px-5 py-3 hover:bg-bg-elevated transition"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar
                      name={member.userName?.split(" ")[0]}
                      surname={member.userName?.split(" ")[1]}
                      image={member.userImage}
                      size="sm"
                    />
                    <div className="min-w-0">
                      <button
                        onClick={() => navigate(`/ingroove/users/${member.userId}`)}
                        className="text-sm text-text-primary font-medium hover:text-primary transition truncate block"
                      >
                        {member.userName}
                      </button>
                      <p className="text-xs text-text-muted truncate">{member.userEmail}</p>
                      <p className="text-xs text-text-hint mt-0.5">{member.ticketName}</p>
                      {member.extras.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {member.extras.map((ex, i) => (
                            <span
                              key={i}
                              className="text-xs bg-secondary/10 text-secondary border border-secondary/20 px-2 py-0.5 rounded-full"
                            >
                              {ex}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0 ml-4 space-y-1.5">
                    <Badge label={member.status} variant={saleStatusVariant(member.status)} />
                    <p className="text-xs text-text-muted">{formatDate(member.purchasedAt)}</p>
                    <p className="text-xs text-text-hint">
                      Paid {formatCurrency(member.totalAmountPaid)}
                    </p>
                    {member.status === "Completed" && (
                      <button
                        onClick={() => setRefundTarget(member)}
                        className="text-xs text-amber-400 hover:opacity-80 transition font-medium block"
                      >
                        Refund {formatCurrency(member.refundableAmount)}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {audienceTotalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border text-xs text-text-muted">
              <span>
                Page {audiencePage} of {audienceTotalPages} — {filteredAudience.length} members
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setAudiencePage((p) => Math.max(1, p - 1))}
                  disabled={audiencePage === 1}
                  className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={13} />
                </button>
                <button
                  onClick={() => setAudiencePage((p) => Math.min(audienceTotalPages, p + 1))}
                  disabled={audiencePage === audienceTotalPages}
                  className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ticket tiers tab */}
      {tab === "tickets" && (
        <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Ticket size={14} className="text-primary" />
            <h3 className="text-sm font-semibold text-text-primary">Ticket Tiers</h3>
          </div>

          {ticketTiers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Ticket size={24} className="text-border-hover mb-2" />
              <p className="text-xs text-text-muted">No ticket tiers</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {ticketTiers.map((tier) => (
                <div key={tier.id} className="px-5 py-4 hover:bg-bg-elevated transition">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm text-text-primary font-medium">{tier.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-text-muted">
                        <span>Price: <span className="text-text-primary">{formatCurrency(tier.price)}</span></span>
                        <span>Hub base: <span className="text-text-secondary">{formatCurrency(tier.hubBasePrice)}</span></span>
                        <span>Fee: <span className="text-amber-400">{formatCurrency(tier.price - tier.hubBasePrice)}</span></span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-primary">
                        {tier.sold} / {tier.quantity}
                      </p>
                      <p className="text-xs text-text-muted">{tier.remaining} remaining</p>
                    </div>
                  </div>
                  <ProgressBar sold={tier.sold} total={tier.quantity} />
                  <div className="flex justify-between text-xs text-text-muted mt-1.5">
                    <span>{((tier.sold / Math.max(tier.quantity, 1)) * 100).toFixed(0)}% sold</span>
                    <span>Revenue: <span className="text-text-primary font-medium">{formatCurrency(tier.sold * tier.hubBasePrice)}</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Extras tab */}
      {tab === "extras" && (
        <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
            <Package size={14} className="text-secondary" />
            <h3 className="text-sm font-semibold text-text-primary">Extras</h3>
          </div>

          {extraTiers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Package size={24} className="text-border-hover mb-2" />
              <p className="text-xs text-text-muted">No extras for this event</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {extraTiers.map((extra) => (
                <div key={extra.id} className="px-5 py-4 hover:bg-bg-elevated transition">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm text-text-primary font-medium">{extra.name}</p>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-text-muted">
                        <span>Price: <span className="text-text-primary">{formatCurrency(extra.price)}</span></span>
                        <span>Hub base: <span className="text-text-secondary">{formatCurrency(extra.hubBasePrice)}</span></span>
                        <span>Fee: <span className="text-amber-400">{formatCurrency(extra.price - extra.hubBasePrice)}</span></span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-secondary">
                        {extra.sold} / {extra.quantity}
                      </p>
                    </div>
                  </div>
                  <ProgressBar sold={extra.sold} total={extra.quantity} color="bg-secondary" />
                  <div className="flex justify-between text-xs text-text-muted mt-1.5">
                    <span>{((extra.sold / Math.max(extra.quantity, 1)) * 100).toFixed(0)}% sold</span>
                    <span>Revenue: <span className="text-text-primary font-medium">{formatCurrency(extra.sold * extra.hubBasePrice)}</span></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Refund modal */}
      {refundTarget && (
        <RefundModal
          member={refundTarget}
          onConfirm={handleRefund}
          onCancel={() => setRefundTarget(null)}
          loading={refunding}
        />
      )}
    </div>
  );
};

export default EventDetail;
